/**
 * Contrato dos pedidos de compra: o funil, os itens e o cálculo de valor.
 *
 * Papel: aqui ficam os cinco conjuntos de valores fixos de onde nascem os enums
 * do Postgres, as funções `valorFinalDoItem` e `totalDoPedido`, as regras de
 * transição do funil em `proximoStatus` e `statusAnterior`, e os schemas que os
 * formulários e as Functions usam.
 *
 * Como usar: o **total não é campo**. É a soma de `valorFinalDoItem` sobre os
 * itens, e quem a calcula é `totalDoPedido`, aqui, para a tela, o relatório e
 * qualquer conferência somarem do mesmo jeito. Guardá-lo numa coluna criaria um
 * segundo lugar onde a mesma verdade estaria escrita, e os dois divergiriam no
 * primeiro item corrigido.
 *
 * O **fornecedor e a situação financeira são do item**, e não do pedido. Um
 * pedido pode ter itens de fornecedores diferentes, e um item pago convivendo
 * com outro provisionado; guardar qualquer um dos dois no cabeçalho produz a
 * cópia que envelhece e passa a contradizer a linha que ela resume.
 */
import { z } from 'zod';

import {
  dataOpcionalSchema,
  dataSchema,
  idSchema,
  textoObrigatorio,
  textoOpcional,
} from './comum';
import { intervaloParcelamentoSchema, parcelaSchema } from './financas';
import { dinheiroComPadraoZeroSchema, valorPorUnidadeSchema } from './formatos';

/**
 * As etapas do funil de um pedido.
 *
 * `aprovado` e `reprovado` estão na lista embora nenhum pedido descanse neles:
 * são o instante da decisão, e o pedido segue dali para `em_processo_de_compra`
 * ou para `finalizado`. Recusá-los no enum obrigaria a decisão e a transição a
 * acontecerem na mesma gravação, e uma delas falharia sem a outra.
 */
export const STATUS_PEDIDO_COMPRA = [
  'em_cotacao',
  'em_aprovacao',
  'aprovado',
  'reprovado',
  'em_processo_de_compra',
  'comprado',
  'entregue',
  'finalizado',
] as const;

/**
 * As etapas que viram coluna no quadro.
 *
 * São as que ficam em repouso. `aprovado` e `reprovado` são instantâneos, e
 * `finalizado` sai do quadro para a tabela de encerrados — um quadro com coluna
 * de finalizados cresceria para sempre e empurraria as outras para fora da tela.
 */
export const STATUS_NO_QUADRO = [
  'em_cotacao',
  'em_aprovacao',
  'em_processo_de_compra',
  'comprado',
  'entregue',
] as const;

/** Por onde o pedido chegou. */
export const CANAL_ORIGEM_PEDIDO = ['whatsapp', 'email', 'presencial'] as const;

/** A natureza do que se está comprando. */
export const CATEGORIA_PEDIDO_COMPRA = [
  'manutencao',
  'decoracao',
  'uniformes',
  'limpeza_e_insumos',
  'equipamentos',
  'alimentos_e_bebidas',
  'outros',
] as const;

/** Como o item foi pago. */
export const FORMA_PAGAMENTO_PEDIDO = [
  'cartao_de_credito',
  'pix',
  'boleto',
  'dinheiro',
  'outro',
] as const;

/** Se o item já saiu do caixa ou está só previsto. */
export const STATUS_FINANCEIRO_PEDIDO = ['pago', 'provisionado'] as const;

/**
 * Quem pode decidir uma aprovação.
 *
 * É sugestão da tela, e não restrição do banco: `aprovador` é texto. A lista
 * muda quando alguém ganha ou perde a alçada, e um enum cobraria migration por
 * isso.
 */
export const APROVADORES_SUGERIDOS = ['GAP', 'LMAP'] as const;

/**
 * Quem pode constar como comprador.
 *
 * Também é sugestão. Os pedidos importados de planilha trazem nomes de pessoas
 * que não cabem nestes três valores, e recusá-los perderia o histórico.
 */
export const COMPRADORES_SUGERIDOS = ['Concierge', 'Equipe Residência', 'Outros'] as const;

export const statusPedidoSchema = z.enum(STATUS_PEDIDO_COMPRA, {
  message: 'Etapa desconhecida.',
});
export const canalOrigemSchema = z.enum(CANAL_ORIGEM_PEDIDO, {
  message: 'Canal desconhecido.',
});
export const categoriaPedidoSchema = z.enum(CATEGORIA_PEDIDO_COMPRA, {
  message: 'Categoria desconhecida.',
});
export const formaPagamentoSchema = z.enum(FORMA_PAGAMENTO_PEDIDO, {
  message: 'Forma de pagamento desconhecida.',
});
export const statusFinanceiroSchema = z.enum(STATUS_FINANCEIRO_PEDIDO, {
  message: 'Situação financeira desconhecida.',
});

export type StatusPedidoCompra = z.infer<typeof statusPedidoSchema>;
export type CanalOrigemPedido = z.infer<typeof canalOrigemSchema>;
export type CategoriaPedidoCompra = z.infer<typeof categoriaPedidoSchema>;
export type FormaPagamentoPedido = z.infer<typeof formaPagamentoSchema>;
export type StatusFinanceiroPedido = z.infer<typeof statusFinanceiroSchema>;

/* ------------------------------------------------------------------- valores */

/** O mínimo de um item para o cálculo de valor. */
export interface ItemComValor {
  quantidade: number;
  valorUnitario: number;
  desconto: number;
  acrescimo: number;
}

/**
 * O valor final de um item, com desconto e acréscimo aplicados.
 *
 * Os dois pisos em zero são regra, e não defesa contra entrada inválida: um
 * desconto maior que o bruto **não** vira crédito a favor da família. Sem o
 * primeiro piso, um desconto digitado com uma casa a mais transformaria a linha
 * num valor negativo que abateria o total do pedido inteiro.
 *
 * @param item - Quantidade, valor unitário, desconto e acréscimo.
 * @returns O valor da linha, nunca negativo.
 */
export function valorFinalDoItem(item: ItemComValor): number {
  const bruto = item.valorUnitario * item.quantidade;
  const comDesconto = Math.max(bruto - item.desconto, 0);
  return Math.max(comDesconto + item.acrescimo, 0);
}

/**
 * A soma bruta dos itens, sem desconto nem acréscimo.
 *
 * Serve de referência ao lado do total: é a diferença entre os dois que mostra
 * quanto de desconto o pedido carrega, e um total sozinho não conta isso.
 *
 * @param itens - Os itens do pedido.
 * @returns A soma de `valorUnitario × quantidade`.
 */
export function totalBrutoDoPedido(itens: readonly ItemComValor[]): number {
  return itens.reduce((soma, item) => soma + item.valorUnitario * item.quantidade, 0);
}

/**
 * O total de um pedido.
 *
 * @param itens - Os itens do pedido, em qualquer ordem.
 * @returns A soma dos valores finais; zero num pedido sem itens.
 */
export function totalDoPedido(itens: readonly ItemComValor[]): number {
  return itens.reduce((soma, item) => soma + valorFinalDoItem(item), 0);
}

/* --------------------------------------------------------------------- funil */

/** O que uma transição exige de quem a dispara. */
export type DecisaoDaTransicao = 'aprovacao' | 'compra' | null;

/**
 * Para onde um pedido vai ao avançar, e o que a etapa exige antes.
 *
 * Duas etapas não avançam sozinhas: sair de `em_aprovacao` pede a decisão de
 * quem aprova, e sair de `em_processo_de_compra` pede quem comprou. Nesses dois
 * casos o status devolvido é o mesmo de entrada, e `decisao` diz o que falta —
 * é o que impede o pedido de atravessar a aprovação sem aprovador registrado.
 *
 * @param status - A etapa atual.
 * @returns A etapa de destino e a decisão que a antecede; destino igual ao
 *   atual significa que a transição depende dessa decisão.
 */
export function proximoStatus(status: StatusPedidoCompra): {
  status: StatusPedidoCompra;
  decisao: DecisaoDaTransicao;
} {
  switch (status) {
    case 'em_cotacao':
      return { status: 'em_aprovacao', decisao: null };
    case 'em_aprovacao':
      return { status: 'em_aprovacao', decisao: 'aprovacao' };
    case 'em_processo_de_compra':
      return { status: 'em_processo_de_compra', decisao: 'compra' };
    case 'comprado':
      return { status: 'entregue', decisao: null };
    case 'entregue':
      return { status: 'finalizado', decisao: null };
    default:
      return { status, decisao: null };
  }
}

/**
 * Para onde um pedido volta ao retroceder, e o que a volta apaga.
 *
 * Retroceder desfaz o que a etapa preencheu, e por isso não é simétrico ao
 * avanço: voltar de `em_processo_de_compra` apaga quem aprovou e quando, e
 * voltar de `comprado` apaga quem comprou. Deixar os campos preenchidos faria o
 * pedido afirmar uma aprovação que ele não tem mais.
 *
 * Um `finalizado` **com motivo de reprovação** volta para `em_aprovacao`, e não
 * para `entregue`: ele nunca passou por lá — foi reprovado e encerrado direto.
 *
 * @param status - A etapa atual.
 * @param foiReprovado - Se o pedido tem motivo de reprovação registrado.
 * @returns A etapa de destino e quais campos a volta limpa.
 */
export function statusAnterior(
  status: StatusPedidoCompra,
  foiReprovado: boolean,
): { status: StatusPedidoCompra; limpar: readonly ('aprovacao' | 'compra' | 'reprovacao')[] } {
  if (status === 'finalizado') {
    return foiReprovado
      ? { status: 'em_aprovacao', limpar: ['reprovacao'] }
      : { status: 'entregue', limpar: [] };
  }

  const posicao = STATUS_NO_QUADRO.findIndex((atual) => atual === status);
  if (posicao <= 0) return { status, limpar: [] };

  const anterior = STATUS_NO_QUADRO[posicao - 1] ?? status;
  if (status === 'em_processo_de_compra') return { status: anterior, limpar: ['aprovacao'] };
  if (status === 'comprado') return { status: anterior, limpar: ['compra'] };
  return { status: anterior, limpar: [] };
}

/* ------------------------------------------------------------------ schemas */

/**
 * A residência do pedido, como a listagem a traz aninhada.
 *
 * Em `pedidoCompraSchema` ela é anulável: o pedido de `Ambas / não se aplica`
 * chega com `residencia: null`, e quem desenha a casa do pedido precisa
 * escrever esse texto no lugar do nome, em vez de supor que o objeto existe.
 */
export const residenciaDoPedidoSchema = z.object({
  id: idSchema,
  nome: z.string(),
  apelido: z.string(),
});

/**
 * A imagem de um item, com três estados que dizem coisas diferentes.
 *
 * Ausente significa **não mexa**: o formulário de edição não carrega as imagens,
 * e um corpo sem `foto` não pode apagar a que está gravada. String vazia ou nulo
 * significam **apague**. Uma string com conteúdo substitui.
 *
 * Sem os três estados, salvar um pedido apagaria as fotos que ninguém pediu para
 * apagar — e é exatamente o que uma normalização de vazio para nulo faria aqui,
 * ao confundir o campo que não veio com o campo esvaziado de propósito.
 *
 * Exportado porque `formulario-pedido.tsx` valida o arquivo escolhido com o
 * mesmo schema antes de gravá-lo no campo do item — recusar no navegador, com a
 * mesma mensagem, evita um `handleSubmit` que falha sem explicação porque o
 * arquivo passou dos 4.000.000 de caracteres em base64 (uns 3 MB de imagem).
 */
export const fotoDeEntradaDoPedidoSchema = z
  .string()
  .max(4_000_000, { message: 'Imagem grande demais.' })
  .nullish();

/**
 * O que o formulário envia para criar ou alterar um item do pedido.
 *
 * `fornecedor` e `statusFinanceiro` vivem aqui, e não no pedido: um pedido pode
 * ter itens de fornecedores diferentes e um item pago ao lado de outro
 * provisionado. O resumo que a tela mostra no cartão é calculado a partir destes
 * campos, nunca guardado em paralelo.
 *
 * `foto` é uma imagem em base64, e por isso o limite é generoso. Nenhuma
 * listagem a devolve — quem a quer abre o item.
 */
export const novoItemPedidoSchema = z.object({
  /**
   * O item que esta linha continua sendo, quando ela já existia.
   *
   * É por ele que a imagem sobrevive à regravação. Casar por posição parece
   * bastar e não basta: remover a primeira linha faz a segunda assumir a posição
   * zero, e ela herdaria a imagem da que saiu — uma foto trocada de item sem
   * ninguém pedir. Ausente significa linha nova, que nasce sem imagem.
   */
  id: idSchema.optional(),
  descricao: textoObrigatorio('Descrição', 300),
  quantidade: z
    .union([z.number(), z.string()])
    .transform((valor) => (typeof valor === 'number' ? valor : Number(valor.trim())))
    .refine((valor) => Number.isInteger(valor) && valor >= 1, {
      message: 'A quantidade precisa ser um número inteiro de ao menos 1.',
    }),
  valorUnitario: valorPorUnidadeSchema,
  desconto: dinheiroComPadraoZeroSchema,
  acrescimo: dinheiroComPadraoZeroSchema,
  fornecedor: textoOpcional(120),
  categoriaFinanceira: textoOpcional(80),
  subcategoriaFinanceira: textoOpcional(80),
  formaPagamento: formaPagamentoSchema.nullish().transform((valor) => valor ?? null),
  statusFinanceiro: statusFinanceiroSchema,
  dataCompra: dataOpcionalSchema,
  /**
   * Em quantas vezes a despesa deste item será lançada.
   *
   * Nulo é à vista, que é o caso de todos os itens registrados até hoje. Um
   * valor a partir de dois faz a automação gerar uma despesa por parcela, com o
   * valor repartido e as datas andando pelo `intervaloParcelamento`.
   */
  parcelas: parcelaSchema,
  intervaloParcelamento: intervaloParcelamentoSchema
    .nullish()
    .transform((valor) => valor ?? null),
  foto: fotoDeEntradaDoPedidoSchema,
});

export type NovoItemPedido = z.infer<typeof novoItemPedidoSchema>;

/**
 * O que o formulário envia para criar ou alterar um pedido.
 *
 * Os itens vêm juntos, e substituem os anteriores por inteiro. O pedido sem item
 * nenhum é aceito: ele nasce em cotação, e a cotação é justamente o momento em
 * que ainda não se sabe o que vai ser comprado.
 *
 * Não há campo de fornecedor nem de situação financeira — os dois são do item.
 * Também não há total: ele é `totalDoPedido` sobre os itens.
 */
export const novoPedidoCompraSchema = z.object({
  /**
   * A casa do pedido, ou `null` no pedido de `Ambas / não se aplica`.
   *
   * O nulo precisa vir escrito: a chave ausente é recusada, porque a alteração
   * substitui o pedido inteiro e um corpo que simplesmente esqueceu o campo
   * tiraria a casa de um pedido sem ninguém ter pedido. Preenchido, continua
   * precisando ser um identificador válido — a string vazia do formulário sem
   * escolha nenhuma é recusada, e não lida como `Ambas / não se aplica`.
   */
  residenciaId: idSchema.nullable(),
  numero: textoObrigatorio('Número', 40),
  data: dataSchema,
  status: statusPedidoSchema,
  solicitante: textoObrigatorio('Solicitante', 120),
  canalOrigem: canalOrigemSchema,
  categoria: categoriaPedidoSchema,
  observacao: textoOpcional(2000),
  aprovador: textoOpcional(120),
  dataAprovacao: dataOpcionalSchema,
  compradoPor: textoOpcional(120),
  motivoReprovacao: textoOpcional(500),
  lancadoNoFinanceiro: z.boolean().default(false),
  itens: z.array(novoItemPedidoSchema).default([]),
});

export type NovoPedidoCompra = z.infer<typeof novoPedidoCompraSchema>;

/** Um item como a API o devolve, sem a foto. */
export const itemPedidoSchema = z.object({
  id: idSchema,
  posicao: z.number().int(),
  descricao: z.string(),
  quantidade: z.number().int(),
  valorUnitario: z.number(),
  desconto: z.number(),
  acrescimo: z.number(),
  fornecedor: z.string().nullable(),
  categoriaFinanceira: z.string().nullable(),
  subcategoriaFinanceira: z.string().nullable(),
  formaPagamento: formaPagamentoSchema.nullable(),
  statusFinanceiro: statusFinanceiroSchema,
  dataCompra: z.string().nullable(),
  parcelas: z.number().int().nullable(),
  intervaloParcelamento: intervaloParcelamentoSchema.nullable(),
  /** Se há foto guardada; o conteúdo dela vem por rota própria. */
  temFoto: z.boolean(),
});

export type ItemPedido = z.infer<typeof itemPedidoSchema>;

/** Um pedido como a API o devolve, com os itens aninhados. */
export const pedidoCompraSchema = z.object({
  id: idSchema,
  residencia: residenciaDoPedidoSchema.nullable(),
  numero: z.string(),
  data: z.string(),
  status: statusPedidoSchema,
  solicitante: z.string(),
  canalOrigem: canalOrigemSchema,
  categoria: categoriaPedidoSchema,
  observacao: z.string().nullable(),
  aprovador: z.string().nullable(),
  dataAprovacao: z.string().nullable(),
  compradoPor: z.string().nullable(),
  motivoReprovacao: z.string().nullable(),
  lancadoNoFinanceiro: z.boolean(),
  itens: z.array(itemPedidoSchema),
  criadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
  atualizadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
});

export type PedidoCompra = z.infer<typeof pedidoCompraSchema>;

/** A foto de um item, como a rota que a devolve a entrega. */
export const fotoDoItemSchema = z.object({
  id: idSchema,
  foto: z.string().nullable(),
});
